// Import timezone support for moment.js
import 'moment-timezone'
import { useSearchParams } from '@remix-run/react'
import classNames from 'classnames'
import moment from 'moment'
import { Children, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'remix-validated-form'
import Button from 'app/components/Button'
import { AppointmentTypeType } from 'app/models/AppointmentType'
import { AvailabilityType } from 'app/models/Availability'
import { LocationJson } from 'app/models/Location'
import { ProviderType } from 'app/models/Provider'
import { SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'
import { KollaAppointment } from 'app/services/kolla.server'
import { useHandleSearchParams } from 'app/utils'
import { DaySlotType, PatientData, PatientStatusType, TimeSlotType, LOOKAHEAD_DAYS } from 'lib/scheduling'
import { filterSlotsByAppointmentType, filterProvidersByAppointmentType } from 'lib/scheduling.client'

/**
 * Context for the SchedulingWizard component that manages all scheduling state
 * and provides it to child components throughout the multi-step appointment booking flow.
 *
 * This context handles:
 * - Current step and navigation state
 * - Selected appointment type, provider, time slot, and patient data
 * - Form completion and validation state
 * - Regular vs override flow mode
 * - Available time slots and provider data
 */
export const SchedulingWizardContext = createContext<{
  // Selected appointment type and all available types
  apptType?: AppointmentTypeType
  appointmentTypes: AppointmentTypeType[]

  // UI state
  appointmentUnavailable: boolean // Whether appointments are unavailable (triggers different UI)
  back: boolean // Whether user navigated backwards
  currentStep: number // Current step in the wizard (1-based)
  isFormComplete: boolean // Whether the appointment form is complete and valid
  totalSteps: number // Total number of steps in the current flow
  isSaving: boolean // Whether appointment is being saved
  flowMode: `regular` | `override` // Regular booking flow vs override flow for unavailable slots
  isDataLoading: boolean // Whether deferred data is still loading

  // Patient and appointment selection
  patientType?: PatientStatusType // "New Patient" or "Existing Patient"
  provider?: ProviderType // Selected provider
  providers: ProviderType[] // All available providers for selected appointment type
  patient?: PatientData // Patient form data
  selectedInsurance?: string // Selected insurance plan

  // Time slot selection
  selectedDaySlotIndex: number // Index of selected day in the slots array
  selectedTimeSlot?: TimeSlotType // Selected time slot
  selectedAvailability?: AvailabilityType // Selected availability (provider + operatory combination)
  daySlots?: DaySlotType[] // All available day slots with time slots

  // Appointment result
  createdAppointment?: KollaAppointment // Successfully created appointment data

  // State setters
  setSelectedInsurance: (insurance?: string) => void
  setApptType: (apptType?: AppointmentTypeType) => void
  setAppointmentUnavailable: (unavailable: boolean) => void
  setBack: (back: boolean) => void
  setIsFormComplete: (isFormComplete: boolean) => void
  setPatientType: (patientType?: PatientStatusType) => void
  setProvider: (provider?: ProviderType) => void
  setPatient: (patient: PatientData) => void
  setSelectedDaySlotIndex: (slotIndex: number) => void
  setSelectedTimeSlot: (slot?: TimeSlotType) => void
  setSelectedAvailability?: (availability?: AvailabilityType) => void
  setCurrentStep: (step: number) => void
  setIsSaving: (isSaving: boolean) => void
  setCreatedAppointment: (appointment?: KollaAppointment) => void
  setTotalSteps: (steps: number) => void
  setFlowMode: (mode: `regular` | `override`) => void
}>({
  apptType: undefined,
  appointmentTypes: [],
  appointmentUnavailable: false,
  back: false,
  currentStep: 1,
  isFormComplete: false,
  patientType: undefined,
  provider: undefined,
  providers: [],
  patient: undefined,
  selectedDaySlotIndex: -1,
  totalSteps: 1,
  isSaving: false,
  flowMode: `regular`,
  isDataLoading: false,
  selectedInsurance: undefined,
  setSelectedInsurance: (insurance?: string) => {},
  setApptType: (apptType?: AppointmentTypeType) => {},
  setAppointmentUnavailable: (unavailable: boolean) => {},
  setBack: (back: boolean) => {},
  setIsFormComplete: (isFormComplete: boolean) => {},
  setPatientType: (patientType?: PatientStatusType) => {},
  setProvider: (provider?: ProviderType) => {},
  setPatient: (patient: PatientData) => {},
  setSelectedDaySlotIndex: (slotIndex: number) => {},
  setSelectedTimeSlot: (slot?: TimeSlotType) => {},
  setSelectedAvailability: (availability?: AvailabilityType) => {},
  setCurrentStep: (step: number) => {},
  setIsSaving: (isSaving: boolean) => {},
  setCreatedAppointment: (appointment?: KollaAppointment) => {},
  setTotalSteps: (steps: number) => {},
  setFlowMode: (mode: `regular` | `override`) => {},
})

/**
 * Main SchedulingWizard component that orchestrates the multi-step appointment booking flow.
 *
 * This component:
 * - Manages all scheduling state via React Context
 * - Processes appointment availability data (prefers server-side processed slots)
 * - Handles automatic step progression based on user selections
 * - Filters available slots based on selected appointment type and provider
 *
 * @param appointmentTypes - All available appointment types for this location
 * @param allProviders - All providers (legacy - used for filtering availabilities)
 * @param allAvailabilities - All availabilities (legacy - used for filtering by appointment type)
 * @param preProcessedSlots - Server-side processed time slots with availability and conflict resolution
 * @param location - Location information for the appointment
 * @param lookaheadDays - Number of days to look ahead for appointments (default from LOOKAHEAD_DAYS)
 * @param startDate - Starting date for appointment availability
 * @param schedulingConfiguration - Location-specific scheduling rules and settings
 * @param children - Child components or render function that receives patient type and slots
 */
function SchedulingWizard({
  appointmentTypes,
  allProviders,
  allAvailabilities,
  preProcessedSlots,
  location,
  lookaheadDays = LOOKAHEAD_DAYS,
  startDate,
  schedulingConfiguration,
  isDataLoading = false,
  children,
}: {
  appointmentTypes: AppointmentTypeType[]
  allProviders?: ProviderType[]
  allAvailabilities?: AvailabilityType[]
  preProcessedSlots: DaySlotType[]
  location?: LocationJson
  lookaheadDays?: number
  startDate: string
  schedulingConfiguration: SchedulingConfigurationJson
  isDataLoading?: boolean
  children: React.ReactNode | ((patientType?: PatientStatusType, slots?: DaySlotType[]) => React.ReactNode)
}) {
  // State variables for the scheduling wizard
  const [apptType, _setApptType] = useState<AppointmentTypeType>()
  const [appointmentUnavailable, setAppointmentUnavailable] = useState<boolean>(true)
  const [back, setBack] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [, setAvailabilities] = useState<AvailabilityType[]>([]) // Legacy - filtered availabilities
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false)
  const [providers, setProviders] = useState<ProviderType[]>([]) // Providers available for selected appointment type
  const [provider, setProvider] = useState<ProviderType | undefined>()
  const [patient, setPatient] = useState<PatientData | undefined>(undefined)
  const [patientType, _setPatientType] = useState<PatientStatusType>()
  const [selectedDaySlotIndex, setSelectedDaySlotIndex] = useState<number>(-1) // Index in the slots array
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotType>()
  const [selectedAvailability, setSelectedAvailability] = useState<AvailabilityType>()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [createdAppointment, setCreatedAppointment] = useState<KollaAppointment | undefined>()
  const [totalSteps, setTotalSteps] = useState<number>(5) // Updated by child components
  const [flowMode, setFlowMode] = useState<`regular` | `override`>(`regular`)
  const [selectedInsurance, setSelectedInsurance] = useState<string>()

  /**
   * Auto-advance to the next step when patient type is selected.
   * Patient type selection is step 1, so advance to step 2 (appointment type selection).
   */
  const setPatientType = (patientType?: PatientStatusType) => {
    _setPatientType(patientType)
    if (currentStep === 1) {
      setCurrentStep(2)
      setBack(false)
    }
  }

  /**
   * Auto-advance to the next step when appointment type is selected.
   * Appointment type selection is step 2, so advance to step 3 (appointment config/time selection).
   */
  const setApptType = (apptType?: AppointmentTypeType) => {
    _setApptType(apptType)
    if (currentStep === 2) {
      setCurrentStep(3)
      setBack(false)
    }
  }

  /**
   * Process and filter available time slots based on selected appointment type and provider.
   *
   * Modern approach: Uses pre-processed slots from the server that already have:
   * - Conflict resolution applied (via removeOverlaps function)
   * - Provider and operatory availability checked
   * - Double booking logic enforced
   *
   * Client-side filtering adds:
   * - Appointment type vendor ID filtering
   * - Provider filtering
   * - Duration validation (ensures enough consecutive slots for appointment length)
   *
   * Legacy approach: Client-side slot generation (maintained for backward compatibility)
   */
  const slots = useMemo(() => {
    // Must have an appointment type selected to show slots
    if (!apptType) return undefined

    // Use shared filtering logic from scheduling.client.ts
    return filterSlotsByAppointmentType(preProcessedSlots, apptType, schedulingConfiguration, provider?.id)
  }, [preProcessedSlots, apptType, provider, schedulingConfiguration])

  /**
   * Legacy effect: Filter providers and availabilities based on selected appointment type.
   * This is used for backward compatibility when not using pre-processed slots.
   *
   * When an appointment type is selected:
   * - Filter availabilities to only those that support the appointment type
   * - Filter providers to only those that have matching availabilities
   */
  useEffect(() => {
    if (allProviders && allAvailabilities && apptType) {
      const availabilities = allAvailabilities.filter(
        (a) =>
          a.appointmentTypeIds?.includes(apptType.vendorId!) &&
          allProviders.find((provider) => provider.id === a.providerId),
      )
      // Use shared filtering logic from scheduling.client.ts
      const providers = filterProvidersByAppointmentType(allProviders, allAvailabilities, apptType)
      setAvailabilities(availabilities)
      setProviders(providers)
    }
  }, [allProviders, allAvailabilities, apptType])

  /**
   * Automatically update the selected day slot index when slots change.
   *
   * Logic:
   * - If the previously selected index still has available time slots, keep it
   * - Otherwise, find the first day that has available time slots and is not in the past
   * - If no slots are available, set to -1 (no selection)
   */
  useEffect(() => {
    setSelectedDaySlotIndex((prev) => {
      if (slots?.length) {
        // If the previous index is still valid and has time slots, keep it
        // Otherwise, find the first index that has time slots and is not in the past
        if (slots[prev]?.timeSlots?.length > 0) {
          return prev
        }
        return slots.findIndex((slot) => {
          // Skip slots with no time slots
          if (!slot?.timeSlots?.length) return false
          // Skip slots in the past
          const slotDate = slot.date
          if (!slotDate) return false
          return !moment(slotDate).isBefore(moment().startOf(`day`))
        })
      }
      // If no slots are available, reset to no selection
      return -1
    })
  }, [slots])

  const context = {
    apptType,
    appointmentTypes,
    appointmentUnavailable,
    back,
    currentStep,
    isFormComplete,
    patientType,
    provider,
    providers,
    patient,
    selectedDaySlotIndex,
    selectedTimeSlot,
    selectedAvailability,
    daySlots: slots,
    totalSteps,
    isSaving,
    createdAppointment,
    flowMode,
    isDataLoading,
    selectedInsurance,
    setSelectedInsurance,
    setApptType,
    setAppointmentUnavailable,
    setBack,
    setCurrentStep,
    setSelectedDaySlotIndex,
    setSelectedTimeSlot,
    setSelectedAvailability,
    setIsFormComplete,
    setPatientType,
    setProvider,
    setPatient,
    setIsSaving,
    setCreatedAppointment,
    setTotalSteps,
    setFlowMode,
  }

  return (
    <SchedulingWizardContext.Provider value={context}>
      {typeof children === `function` ? children(patientType, slots) : children}
    </SchedulingWizardContext.Provider>
  )
}

/**
 * Component that renders the current step of the scheduling wizard.
 *
 * This component:
 * - Displays only the current step based on the wizard state
 * - Manages step-specific styling and layout
 * - Automatically updates the total step count when children change
 * - Handles different heights for different steps (form step needs more space)
 *
 * @param children - Array of step components to render
 */
export const SchedulingWizardSteps = ({ children }) => {
  const { appointmentUnavailable, currentStep, flowMode, setTotalSteps } = useContext(SchedulingWizardContext)
  const steps = Children.toArray(children)
  const step = steps[currentStep - 1] as React.ReactElement

  // Update total steps when children change (fixes React setState warning)
  useEffect(() => setTotalSteps(steps.length), [children])

  return (
    <div
      className={classNames(`z-40 flex h-full w-full flex-col overflow-hidden bg-white p-6`, {
        // Standard height for most steps
        'md:h-[668px]': flowMode === `regular` ? currentStep !== 4 : currentStep !== 3,
        // Taller height for form step (step 4 in regular flow, step 3 in override flow)
        'md:max-h-[78vh] md:min-h-[668px]': flowMode === `regular` ? currentStep === 4 : currentStep === 3,
        // Rounded bottom corners when appointments are unavailable
        'md:rounded-b-xl': appointmentUnavailable,
      })}
    >
      {step}
    </div>
  )
}

/**
 * Component that renders the navigation buttons (Back/Next/Submit) for the scheduling wizard.
 *
 * This component:
 * - Manages step navigation and validation
 * - Handles different validation rules for regular vs override flows
 * - Controls button states (disabled, loading) based on current step and form completion
 * - Manages URL search params (removes 'day' param when going back)
 * - Resets selection state when navigating backwards
 * - Triggers form submission on the final step
 *
 * @param schedulingConfiguration - Location-specific scheduling rules and settings
 */
export const SchedulingWizardButtons = ({
  schedulingConfiguration,
}: {
  schedulingConfiguration?: SchedulingConfigurationJson
}) => {
  const {
    apptType,
    selectedDaySlotIndex,
    currentStep,
    isFormComplete,
    selectedTimeSlot,
    patientType,
    totalSteps,
    selectedAvailability,
    isSaving,
    flowMode,
    daySlots,
    selectedInsurance,
    setBack,
    setCurrentStep,
    setSelectedAvailability,
    setSelectedTimeSlot,
    setProvider,
  } = useContext(SchedulingWizardContext)
  const formContext = useFormContext(`appointment-form`)
  const searchParams = useSearchParams()[0]
  const handleSearchParams = useHandleSearchParams()

  /**
   * Determine if the Next/Submit button should be disabled based on current step and selections.
   *
   * Override flow validation (fewer steps):
   * - Step 1: Patient type must be selected
   * - Step 2: Appointment type must be selected
   * - Step 3: Form must be complete (final step)
   *
   * Regular flow validation:
   * - Step 1: Patient type must be selected
   * - Step 2: Appointment type must be selected
   * - Step 3: Time slot and availability must be selected
   * - Step 4: Form must be complete AND insurance validation must pass
   */
  const isNextDisabled = useMemo(() => {
    if (flowMode === `override`) {
      switch (currentStep) {
        case 1:
          return !patientType // Must select "New Patient" or "Existing Patient"
        case 2:
          return !apptType // Must select an appointment type
        case 3:
          return !isFormComplete // Must complete the appointment form (final step)
        default:
          return true // Unknown step, disable button
      }
    }

    // Regular flow validation
    switch (currentStep) {
      case 1:
        return !patientType // Must select patient type
      case 2:
        return !apptType // Must select appointment type
      case 3:
        // Must select both a time slot and specific availability (provider + operatory)
        return !selectedAvailability || !selectedTimeSlot
      case 4:
        return (
          !selectedAvailability ||
          !selectedTimeSlot ||
          !isFormComplete ||
          // Additional insurance validation: block submission if insurance is rejected
          (schedulingConfiguration?.notifyInOutNetwork &&
            !!selectedInsurance &&
            schedulingConfiguration?.insurancesRejected?.includes(selectedInsurance))
        )
      default:
        return true // Unknown step, disable button
    }
  }, [
    apptType,
    daySlots,
    currentStep,
    isFormComplete,
    patientType,
    selectedAvailability,
    selectedTimeSlot,
    flowMode,
    selectedDaySlotIndex,
    selectedInsurance,
    schedulingConfiguration,
  ])

  return (
    <div
      className={classNames(
        `z-50 flex w-full shrink-0 justify-between overflow-hidden rounded-none border-t bg-white px-6 py-3 md:z-0 md:rounded-b-xl`,
        {
          hidden: currentStep === totalSteps, // Hide buttons on final confirmation step
          'h-16': currentStep === 1, // Specific height for first step
        },
      )}
      style={{ borderColor: `rgba(0, 0, 0, 0.06)` }}
    >
      {/* Back Button */}
      <Button
        mode="white"
        label="Back"
        className={classNames(
          currentStep > 1 ? `` : `hidden`, // Only show if not on first step
          `mr-2`,
          `h-10 w-1/2 text-center text-label-sm md:w-auto`,
          {
            hidden: currentStep === totalSteps, // Hide on final step
          },
        )}
        size="xl"
        disabled={isSaving} // Disable while saving appointment
        onClick={() => {
          setBack(true)

          // Clear day parameter from URL when going back (allows date reselection)
          if (searchParams.has(`day`)) {
            handleSearchParams({ day: undefined })
          }

          // Reset selections when going back from appointment config step
          if (currentStep === 3) {
            setSelectedAvailability?.(undefined)
            setProvider(undefined)
            setSelectedTimeSlot(undefined)
          }

          // Navigate to previous step
          let step = currentStep <= totalSteps && currentStep > 1 ? currentStep - 1 : currentStep
          setCurrentStep(step)
        }}
      />

      {/* Next/Submit Button */}
      <Button
        mode="primary"
        label={currentStep === totalSteps - 1 ? `Submit` : `Next`}
        className={classNames(`h-10 w-1/2 border-0 text-center text-label-sm md:w-auto`, {
          [`cursor-not-allowed opacity-50`]: isNextDisabled,
          [`hidden`]: currentStep < 3, // Hide Next button on first two steps (auto-advance)
        })}
        size="xl"
        disabled={isNextDisabled}
        loading={isSaving} // Show loading spinner while saving
        onClick={() => {
          setBack(false)

          // Submit form on final step, otherwise advance to next step
          if (currentStep === totalSteps - 1) {
            formContext.submit() // Trigger form submission to API
          } else {
            let step = currentStep < totalSteps ? currentStep + 1 : currentStep
            setCurrentStep(step)
          }
        }}
      />
    </div>
  )
}

SchedulingWizard.Steps = SchedulingWizardSteps
SchedulingWizard.Buttons = SchedulingWizardButtons

export default SchedulingWizard
